import { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { PlatformReport } from 'shared/base';

import { FeatureFlag, useFeatureFlags } from 'shared/domains/featureFlag';

import { ReportYamlEditorWrapper } from '../../controls/yamlEditorWrapper';
import { useReportEditor } from '../../hooks/useReportEditor';
import { useTemplate } from '../../hooks/useTemplate';
import { generateReportObjectWithYamlFromTemplate } from '../../utils';
import { Report } from '../report';

export function ReportPreview({
  handleBackAction,
  templateId,
}: {
  handleBackAction: () => void;
  templateId: string;
}) {
  const { featureFlags } = useFeatureFlags();
  const { creating: allowCreatingReports } = featureFlags[FeatureFlag.ALLOWED_CUSTOM_REPORTS];
  const { handleCreateFromTemplate, handleCreateFromYaml, isCreateFromScratchTemplate } =
    useTemplate(templateId);
  const [previewReport, setPreviewReport] = useState<{ content: string }>();
  const {
    canRedoChanges,
    canUndoChanges,
    editorYaml,
    filters,
    groupedWidgets,
    handleRedoChanges,
    handleSave,
    handleUndoChanges,
    handleViewPreview,
    isReportYamlModified,
    longDescription,
    previewReportYaml,
    setEditorOpen,
    setEditorYaml,
    shortDescription,
    title,
    widgets,
    yamlParsingError,
  } = useReportEditor(previewReport as PlatformReport);

  useEffect(() => {
    const fetchReport = async () => {
      const report = await generateReportObjectWithYamlFromTemplate(templateId);
      setPreviewReport(report);
    };

    fetchReport();
  }, [templateId]);

  if (!previewReport) {
    return null;
  }

  return (
    <ReportYamlEditorWrapper
      canRedoChanges={canRedoChanges}
      canUndoChanges={canUndoChanges}
      currentlyViewedReportYaml={previewReportYaml}
      editorOpen={isCreateFromScratchTemplate}
      editorYaml={editorYaml}
      handleRedoChanges={handleRedoChanges}
      handleSave={handleSave}
      handleUndoChanges={handleUndoChanges}
      handleViewPreview={handleViewPreview}
      isNew={isCreateFromScratchTemplate}
      isReportYamlModified={isReportYamlModified}
      onCancel={() => handleBackAction()}
      onCreate={() => handleCreateFromYaml(editorYaml!, title!, shortDescription!)}
      setEditorOpen={setEditorOpen}
      setEditorYaml={setEditorYaml}
      yamlParsingError={yamlParsingError}
    >
      <Report
        controls={
          <>
            {!isCreateFromScratchTemplate && (
              <Stack direction="row" spacing={1}>
                <Button
                  color="secondary"
                  onClick={() => handleBackAction()}
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                >
                  {allowCreatingReports ? 'Back to templates' : 'Back'}
                </Button>
                {allowCreatingReports && (
                  <Button
                    endIcon={<AddIcon />}
                    onClick={() => handleCreateFromTemplate()}
                    variant="contained"
                  >
                    Use this template
                  </Button>
                )}
              </Stack>
            )}
          </>
        }
        description={shortDescription || ''}
        filters={filters}
        groups={groupedWidgets || []}
        items={widgets || []}
        tipMarkdown={longDescription || ''}
        title={title || ''}
      />
    </ReportYamlEditorWrapper>
  );
}
